import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QrCode, Info } from "lucide-react";
import { Field } from "../../../components/neu/Field";
import { NeuButton } from "../../../components/neu/Neu";
import { Toggle } from "./Toggle";
import { QrZoneCard } from "./QrZoneCard";
import { useQrZones } from "./useQrZones";
import { useFeatures } from "../../../hooks/useFeatures";

// تبويب رموز QR: إلزام الفرع بمسح الرمز + مناطق (طوابق/بوابات) لكل منها رمز مطبوع.
// شرح: الحضور بالوجه+الموقع يكفي للمواقع الأفقية؛ المباني العمودية قد تحتاج QR لكل طابق (اختياري).
export const QrTab = ({ form, set, branchId }) => {
  const { t } = useTranslation();
  const features = useFeatures();
  const { zones, loading, add, rotate, toggle, remove } = useQrZones(branchId);
  const [draft, setDraft] = useState({ name: "", floor: "" });

  const submit = async () => {
    if (!draft.name.trim()) return;
    await add({ name: draft.name.trim(), floor: draft.floor.trim() || null });
    setDraft({ name: "", floor: "" });
  };

  return (
    <div className="grid gap-5" data-testid="branch-qr-tab">
      <p className="flex items-start gap-2 rounded-2xl bg-primary/5 p-4 text-xs leading-6 text-muted-foreground" data-testid="branch-qr-why">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        {t("qr.why")}
      </p>

      <div className="rounded-2xl p-4 shadow-neu-in">
        <Toggle
          label={t("qr.requiredLabel")}
          testId="branch-qr_required"
          value={!!form.qr_required}
          onChange={(v) => set("qr_required", v)}
        />
        <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="branch-qr-hint">
          <QrCode className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          {form.qr_required ? t("qr.requiredOnHint") : t("qr.requiredOffHint")}
        </p>
      </div>

      {!form.qr_required ? null : !branchId ? (
        <p className="text-xs text-muted-foreground" data-testid="qr-save-first">
          {t("qr.saveFirst")}
        </p>
      ) : !features.qr_checkin ? (
        <p className="flex items-start gap-2 rounded-2xl bg-amber-500/10 p-4 text-xs leading-6 text-amber-600" data-testid="qr-feature-off">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {t("qr.featureOff")}
        </p>
      ) : (
        <>
          <p className="text-xs font-semibold text-primary" data-testid="qr-generate-hint">{t("qr.generateHint")}</p>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label={t("qr.zoneName")} testId="qr-zone-name" value={draft.name}
              onChange={(v) => setDraft((d) => ({ ...d, name: v }))} />
            <Field label={t("qr.floor")} testId="qr-zone-floor" value={draft.floor}
              onChange={(v) => setDraft((d) => ({ ...d, floor: v }))} />
            <NeuButton variant="primary" type="button" data-testid="qr-zone-add" onClick={submit}>
              {t("qr.addZone")}
            </NeuButton>
          </div>

          {loading ? (
            <p className="text-xs text-muted-foreground">{t("app.loading")}</p>
          ) : zones.length ? (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="qr-zone-list">
              {zones.map((zone) => (
                <QrZoneCard key={zone.id} zone={zone} branchName={form.name}
                  onRotate={rotate} onToggle={toggle} onRemove={remove} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="qr-zone-empty">{t("qr.empty")}</p>
          )}
        </>
      )}
    </div>
  );
};
